import React, { useState, useEffect } from 'react';
import Sidebar from '../components/SideBar';
import MapaEmpreendimentos, { Imovel, ComercioMarker, ComercioSimples } from '../components/mapas/MapaEmpreendimentos';
import FiltrosDashboard from '../components/FiltrosDashboard';
import PlanoSelector from '../components/PlanoSelector';
import { Filtros } from '../API/filterService';
import { 
  ImovelMapa, 
  getMunicipiosMapa, 
  MunicipioMapaResponse, 
  fetchEmpreendimentosMapa,
  ResumoMapa,
  fetchComerciosMapa,
  ComercioMapa,
} from '../API/mapasService';

import { authService } from "../API/auth";
import type { Plano } from "../components/PlanoSelector";

export default function Mapa() {
  const [planosSelecionados, setPlanosSelecionados] = useState<Plano[]>([]);
  const [planosDisponiveis, setPlanosDisponiveis] = useState<Plano[]>([]);
  const [filtros, setFiltros] = useState<Filtros>({});
  const [imoveisMapa, setImoveisMapa] = useState<Imovel[]>([]);
  const [resumoMapa, setResumoMapa] = useState<ResumoMapa | null>(null);
  const [municipiosMapa, setMunicipiosMapa] = useState<MunicipioMapaResponse["features"]>([]);
  const [centroGeral, setCentroGeral] = useState<{ latitude: number; longitude: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [comerciosMapa, setComerciosMapa] = useState<ComercioSimples[]>([]);
  const [loadingComercio, setLoadingComercio] = useState(false);


  const gerarResumo = (): ResumoMapa => ({
    preco_m2_medio: 0,
    preco_medio_produto: 0,
    valor_minimo: 0,
    valor_maximo: 0,
    valor_medio: 0,
    area_media: 0,
    unidades_lancadas: 0,
    unidades_vendidas: 0,
    unidades_distrato: 0,
    total_unidades: 0,
    vso: 0,
    ofertas_mes_anterior: 0,
    vendas_mes_atual: 0,
  });

  const handleFiltrar = (novosFiltros: Filtros) => setFiltros(novosFiltros);

  const formatarImoveis = (data: ImovelMapa[] = []): Imovel[] =>
    data.map(imovel => ({
      id: String(imovel.id),
      nome: imovel.nome,
      construtora: imovel.construtora,
      bairro: imovel.bairro,
      tipo: Array.isArray(imovel.tipo) ? imovel.tipo : [imovel.tipo || ''],
      preco: imovel.preco ?? undefined,
      status: imovel.status,
      unidadesTotais: imovel.unidades_totais,
      vendidosPercent: imovel.perc_vendido,
      latitude: imovel.latitude,
      longitude: imovel.longitude,
    }));

  const formatarComercios = (data: ComercioMapa[] = []): ComercioMarker[] =>
    data.map(c => ({
      id: c.id,
      nome_fantasia: c.nome_fantasia,
      latitude: c.latitude,
      longitude: c.longitude,
      cnae_codigo: c.cnae_codigo,
      cnpj: c.cnpj,
      cnae_principal: c.cnae_principal,
      cnaes_secundarios: c.cnaes_secundarios ?? [],
      descricao_endereco: c.descricao_endereco,
      municipio: c.municipio,
      bairro: c.bairro,
      telefone_principal: c.telefone_principal,
      telefone_secundario: c.telefone_secundario,
      email: c.email,
      situacao_cadastral: c.situacao_cadastral,
      data_situacao_cadastral: c.data_situacao_cadastral,
      motivo_situacao_cadastral: c.motivo_situacao_cadastral,
      data_inicio_atividade: c.data_inicio_atividade,
      location: c.location,
      distancia: c.distancia,
      categoria: c.categoria,
      endereco: c.endereco,
    }));



  const handlePoligonoCriado = async (geojson: any) => {
    setLoading(true);
    try {
      const data = await fetchEmpreendimentosMapa(filtros, geojson);
      setResumoMapa(data.resumo);
      setImoveisMapa(formatarImoveis(data.empreendimentos));
    } catch (error) {
      console.error(error);
      setResumoMapa(gerarResumo());
      setImoveisMapa([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePoligonoComercios = async (geojson: any) => {
    setLoading(true);
    try {
      const data = await fetchComerciosMapa(filtros, geojson);
      setComerciosMapa(formatarComercios(data.comercios));
    } catch (error) {
      console.error(error);
      setComerciosMapa([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const carregarPerfil = async () => {
      try {
        const user = await authService.getProfile();

        const planos = (user.empresa_membership?.segmentos_acesso ?? []).filter(
          (p: string): p is Plano =>
            ["casa_apartamento", "compacto", "comercial", "lote"].includes(p)
        );

        setPlanosDisponiveis(planos);
      } catch (error) {
        console.error("Erro ao carregar perfil:", error);
        setPlanosDisponiveis([]);
      }
    };

    carregarPerfil();
  }, []);


  useEffect(() => {
    const fetchDadosMapa = async () => {
      setLoading(true);
      try {
        const data = await fetchEmpreendimentosMapa(filtros);
        setResumoMapa(data.resumo);
        setImoveisMapa(formatarImoveis(data.empreendimentos));
        const comerciosData = await fetchComerciosMapa(filtros);
        setComerciosMapa(formatarComercios(comerciosData.comercios));

        const municipiosResponse = await getMunicipiosMapa(filtros.municipio);
        setMunicipiosMapa(municipiosResponse.features ?? []);
        setCentroGeral(municipiosResponse.centroGeral ?? null);
      } catch (error) {
        console.error(error);
        setResumoMapa(gerarResumo());
        setImoveisMapa([]);
        setMunicipiosMapa([]);
        setCentroGeral(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDadosMapa();
  }, [filtros]);

  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <div style={{ padding: 20, background: '#f5f5f5', minHeight: '100vh', flex: 1 }}>
        <PlanoSelector 
          planosDisponiveis={planosDisponiveis}
          planosSelecionados={planosSelecionados}
          onChange={setPlanosSelecionados}
        />
        <FiltrosDashboard planosSelecionados={planosSelecionados} onFiltrar={handleFiltrar} mostrarBotaoRelatorio={false} />
        <MapaEmpreendimentos
          imoveis={imoveisMapa}
          resumo={resumoMapa}
          municipios={municipiosMapa}
          loading={loading}
          initialViewState={centroGeral ? { ...centroGeral, zoom: 11 } : undefined}
          onPoligonoEmpreendimentos={handlePoligonoCriado}
          onPoligonoComercios={handlePoligonoComercios}
          comercios={comerciosMapa}
          onUpdateComercios={setComerciosMapa}
        />
      </div>
    </div>
  );
}
